/*
 * Copyright 2022 Siemens
 */

export type ObserverInterface<T = Record<string, unknown> | null, U extends Symbol | string | object = Symbol> = Omit<
  Observer<T, U>,
  'dispatch' | 'removeAllObservers'
>;

export class Observer<T = Record<string, unknown> | null, U extends Symbol | string | object = Symbol> {
  readonly #observers = new Map<U, (value: T) => void>();

  addObserver(id: U, observer: (value: T) => void): void {
    if (this.#observers.has(id)) console.debug('Observer', id, 'is overwritten');
    this.#observers.set(id, observer);
  }

  removeObserver(id: U): boolean {
    return this.#observers.delete(id);
  }

  removeAllObservers(): void {
    this.#observers.clear();
  }

  dispatch(value: T): boolean {
    for (const observer of this.#observers.values())
      try {
        observer(value);
      } catch {}
    return this.#observers.size > 0;
  }
}

export type SubjectInterface<T = Record<string, unknown> | null, U extends Symbol | string | object = Symbol> = Omit<
  Subject<T, U>,
  'dispatch' | 'dispatchUnique' | 'removeAllObservers'
>;

export class Subject<T = Record<string, unknown> | null, U extends Symbol | string | object = Symbol> extends Observer<T, U> {
  #currentValue: T;

  constructor(defaultValue: T) {
    super();
    this.#currentValue = defaultValue;
  }

  override addObserver(id: U, observer: (value: T) => void, ignoreCurrentValue?: true): void {
    super.addObserver(id, observer);
    if (ignoreCurrentValue) return;
    try {
      observer(this.#currentValue);
    } catch {}
  }

  get value(): T {
    return this.#currentValue;
  }

  override dispatch(value: T): boolean {
    this.#currentValue = value;
    return super.dispatch(value);
  }

  dispatchUnique(value: T): boolean {
    if (this.#currentValue === value) return false;
    return this.dispatch(value);
  }
}
